/* eslint-disable react-refresh/only-export-components */
import { STORAGE_KEY } from "@/constants";
import { queryClient } from "@/hooks/use-query-client";
import { emitEvent, EventConstants } from "@/lib/event-emitter";
import { lowerCaseComparison } from "@/lib/string-util";
import type { IUser } from "@/model/user";
import { disconnectSocket } from "@/services/csr-chat/socket-service";
import { resetcostEstimatorFilter } from "@/store/price transparency/price-transparency-slice";
import { REDUX_STORE } from "@/store/root-reducer";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";

export enum AuthMode {
  GUEST = "guest",
  MEMBER = "member",
  SSO_MEMBER = "sso_member",
}
export type IAuthData = {
  user: IUser | null;
  mode: AuthMode | null;
};

type AuthContextType = {
  authData: IAuthData | null;
  updateAuthData: (data: IAuthData) => Promise<void>;
  logout: () => void;
};

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const dispatch = useDispatch();
  const [authData, setAuthData] = useState<IAuthData | null>(() => {
    // Load authData from session storage on initialization
    const storedAuthData = sessionStorage.getItem(STORAGE_KEY.AUTH_DATA);
    return storedAuthData ? JSON.parse(storedAuthData) : null;
  });

  const timerRef = useRef<number | null>(null);

  const resetTimer = (isTimerTriggered: boolean) => {
    // console.log(
    //   "resetTimer: ",
    //   timerRef.current,
    //   "document.visibilityState: ",
    //   document.visibilityState,
    //   "isTimerTriggered: ",
    //   isTimerTriggered,
    // );
    if (document.visibilityState === "visible" && timerRef.current) {
      //emitEvent(EventConstants.REFRESH_TOKEN);
      startTimer();
    } else if (
      document.visibilityState === "hidden" &&
      timerRef.current &&
      isTimerTriggered
    ) {
      // console.log("Stopping timer");
      if (timerRef.current) {
        // console.log("Stopping timer- emitting session expired");
        window.clearTimeout(timerRef.current); // Clear existing timer
        timerRef.current = null;
        emitEvent(EventConstants.SESSION_EXPIRED);
      }
    }
  };

  const startTimer = () => {
    // console.log("Starting timer");
    if (timerRef.current) {
      window.clearTimeout(timerRef.current); // Clear existing timer
      timerRef.current = null;
    }
    timerRef.current = window.setTimeout(() => resetTimer(true), 1200 * 1000);
    // timerRef.current = window.setTimeout(resetTimer, authData?.user.expires_in);
  };

  useEffect(() => {
    if (authData?.user) {
      startTimer();
    }

    const controller = new AbortController();
    const { signal } = controller;

    // document.addEventListener("visibilitychange", () => resetTimer(false), {
    //   signal,
    // });

    signal.addEventListener("abort", () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authData]);

  const updateAuthData = async (authResponse: IAuthData): Promise<void> => {
    await setAuthData(authResponse);
    await sessionStorage.setItem(
      STORAGE_KEY.AUTH_DATA,
      JSON.stringify(authResponse),
    );
  };

  const logout = () => {
    // console.log("Logging out the user");
    if (lowerCaseComparison(authData?.mode, AuthMode.GUEST)) {
      setAuthData(null);
      sessionStorage.clear();
      queryClient.clear();
      queryClient.invalidateQueries();
      return;
    } else {
      setAuthData(null);
      // Clear authData from session storage

      sessionStorage.removeItem(STORAGE_KEY.AUTH_DATA);
      sessionStorage.removeItem(STORAGE_KEY.APP_INSTANCE_ID);
      sessionStorage.removeItem(STORAGE_KEY.ALERT_DIALOG_STATUS);
      sessionStorage.removeItem(STORAGE_KEY.APP_INTERACTION);
      sessionStorage.removeItem(STORAGE_KEY.CHAT_LIABILITY_ALERT_DISMISSED);
      dispatch(resetcostEstimatorFilter(null));
      dispatch({ type: REDUX_STORE.ACTION.LOGOUT });
      // console.log("Clearing query client");
      queryClient.clear();
      disconnectSocket();
    }
  };

  return (
    <AuthContext.Provider value={{ authData, updateAuthData, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default useAuth;
